import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  serverTimestamp,
  increment,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { db } from '../lib/firebase';

// Report Types
export interface ContentReport {
  id: string;
  reporterId: string;
  reporterName: string;
  contentId: string;
  contentType: 'movie' | 'series' | 'short' | 'comic' | 'novel' | 'comment';
  contentTitle: string;
  contentCreatorId: string;
  contentCreatorName: string;
  reportType: 'inappropriate' | 'spam' | 'copyright' | 'harassment' | 'violence' | 'hate_speech' | 'other';
  description: string;
  evidence?: string[]; // URLs to screenshots or evidence
  status: 'pending' | 'reviewing' | 'resolved' | 'dismissed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  adminNotes?: string;
  resolvedBy?: string;
  resolvedAt?: any;
  createdAt: any;
  updatedAt: any;
}

export interface UserReport {
  id: string;
  reporterId: string;
  reporterName: string;
  reportedUserId: string;
  reportedUserName: string;
  reportType: 'harassment' | 'spam' | 'fake_account' | 'inappropriate_behavior' | 'other';
  description: string;
  evidence?: string[];
  status: 'pending' | 'reviewing' | 'resolved' | 'dismissed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  adminNotes?: string;
  resolvedBy?: string;
  resolvedAt?: any;
  createdAt: any;
  updatedAt: any;
}

export interface DMCARequest {
  id: string;
  claimantName: string;
  claimantEmail: string;
  claimantPhone?: string;
  claimantAddress: string;
  contentId: string;
  contentTitle: string;
  contentCreatorId: string;
  contentCreatorName: string;
  copyrightWork: string;
  originalWorkUrl?: string;
  description: string;
  goodFaithStatement: boolean;
  perjuryStatement: boolean;
  signature: string;
  status: 'pending' | 'processing' | 'takedown_issued' | 'counter_claim_received' | 'resolved';
  adminNotes?: string;
  processedBy?: string;
  processedAt?: any;
  counterClaim?: {
    contentCreatorName: string;
    contentCreatorEmail: string;
    contentCreatorPhone?: string;
    contentCreatorAddress: string;
    description: string;
    goodFaithStatement: boolean;
    perjuryStatement: boolean;
    signature: string;
    submittedAt: any;
  };
  createdAt: any;
  updatedAt: any;
}

export interface UserViolation {
  id: string;
  userId: string;
  userName: string;
  violationType: 'content_violation' | 'harassment' | 'spam' | 'copyright' | 'terms_violation';
  description: string;
  severity: 'warning' | 'strike_1' | 'strike_2' | 'strike_3' | 'suspension' | 'permanent_ban';
  contentId?: string;
  reportId?: string;
  adminNotes?: string;
  issuedBy: string;
  issuedAt: any;
  expiresAt?: any; // For temporary suspensions
  status: 'active' | 'expired' | 'appealed' | 'overturned';
}

export interface ModerationStats {
  totalReports: number;
  pendingReports: number;
  resolvedReports: number;
  totalDMCARequests: number;
  pendingDMCARequests: number;
  totalViolations: number;
  activeViolations: number;
  bannedUsers: number;
  suspendedUsers: number;
}

// Content Reporting
export class ContentModerationService {
  // Report Content
  static async reportContent(
    reporterId: string,
    reporterName: string,
    contentId: string,
    contentType: ContentReport['contentType'],
    contentTitle: string,
    contentCreatorId: string,
    contentCreatorName: string,
    reportType: ContentReport['reportType'],
    description: string,
    evidence?: string[]
  ): Promise<string> {
    // Import sanitization utilities
    const { sanitizeText, sanitizeURL, sanitizeStringArray } = await import('../utils/inputSanitizer');
    try {
      // Sanitize all inputs
      const sanitizedDescription = sanitizeText(description, 2000);
      const sanitizedEvidence = evidence ? evidence.map(url => sanitizeURL(url) || sanitizeText(url, 500)).filter(Boolean) as string[] : [];
      
      const reportData: Omit<ContentReport, 'id'> = {
        reporterId: sanitizeText(reporterId, 100),
        reporterName: sanitizeText(reporterName, 100),
        contentId: sanitizeText(contentId, 100),
        contentType,
        contentTitle: sanitizeText(contentTitle, 200),
        contentCreatorId: sanitizeText(contentCreatorId, 100),
        contentCreatorName: sanitizeText(contentCreatorName, 100),
        reportType,
        description: sanitizedDescription,
        evidence: sanitizedEvidence,
        status: 'pending',
        priority: this.determinePriority(reportType),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'contentReports'), reportData);
      return docRef.id;
    } catch (error) {
      console.error('Error reporting content:', error);
      throw error;
    }
  }

  // Report User
  static async reportUser(
    reporterId: string,
    reporterName: string,
    reportedUserId: string,
    reportedUserName: string,
    reportType: UserReport['reportType'],
    description: string,
    evidence?: string[]
  ): Promise<string> {
    // Import sanitization utilities
    const { sanitizeText, sanitizeURL } = await import('../utils/inputSanitizer');
    try {
      // Sanitize all inputs
      const sanitizedDescription = sanitizeText(description, 2000);
      const sanitizedEvidence = evidence ? evidence.map(url => sanitizeURL(url) || sanitizeText(url, 500)).filter(Boolean) as string[] : [];
      
      const reportData: Omit<UserReport, 'id'> = {
        reporterId: sanitizeText(reporterId, 100),
        reporterName: sanitizeText(reporterName, 100),
        reportedUserId: sanitizeText(reportedUserId, 100),
        reportedUserName: sanitizeText(reportedUserName, 100),
        reportType,
        description: sanitizedDescription,
        evidence: sanitizedEvidence,
        status: 'pending',
        priority: this.determinePriority(reportType),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'userReports'), reportData);
      return docRef.id;
    } catch (error) {
      console.error('Error reporting user:', error);
      throw error;
    }
  }

  // Submit DMCA Request
  static async submitDMCARequest(
    claimantName: string,
    claimantEmail: string,
    claimantPhone: string | undefined,
    claimantAddress: string,
    contentId: string,
    contentTitle: string,
    contentCreatorId: string,
    contentCreatorName: string,
    copyrightWork: string,
    originalWorkUrl: string | undefined,
    description: string,
    goodFaithStatement: boolean,
    perjuryStatement: boolean,
    signature: string
  ): Promise<string> {
    // Import sanitization utilities
    const { sanitizeText, sanitizeEmail, sanitizeURL } = await import('../utils/inputSanitizer');
    try {
      // Sanitize all inputs
      const sanitizedEmail = sanitizeEmail(claimantEmail);
      if (!sanitizedEmail) {
        throw new Error('Invalid email address');
      }
      
      const dmcaData: Omit<DMCARequest, 'id'> = {
        claimantName: sanitizeText(claimantName, 200),
        claimantEmail: sanitizedEmail,
        claimantPhone: claimantPhone ? sanitizeText(claimantPhone, 50) : undefined,
        claimantAddress: sanitizeText(claimantAddress, 500),
        contentId: sanitizeText(contentId, 100),
        contentTitle: sanitizeText(contentTitle, 200),
        contentCreatorId: sanitizeText(contentCreatorId, 100),
        contentCreatorName: sanitizeText(contentCreatorName, 100),
        copyrightWork: sanitizeText(copyrightWork, 500),
        originalWorkUrl: originalWorkUrl ? sanitizeURL(originalWorkUrl) || undefined : undefined,
        description: sanitizeText(description, 5000),
        goodFaithStatement,
        perjuryStatement,
        signature: sanitizeText(signature, 200),
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'dmcaRequests'), dmcaData);
      return docRef.id;
    } catch (error) {
      console.error('Error submitting DMCA request:', error);
      throw error;
    }
  }

  // Submit Counter-Claim
  static async submitCounterClaim(
    dmcaRequestId: string,
    contentCreatorName: string,
    contentCreatorEmail: string,
    contentCreatorPhone: string | undefined,
    contentCreatorAddress: string,
    description: string,
    goodFaithStatement: boolean,
    perjuryStatement: boolean,
    signature: string
  ): Promise<void> {
    try {
      const counterClaim = {
        contentCreatorName,
        contentCreatorEmail,
        contentCreatorPhone,
        contentCreatorAddress,
        description,
        goodFaithStatement,
        perjuryStatement,
        signature,
        submittedAt: serverTimestamp()
      };

      await updateDoc(doc(db, 'dmcaRequests', dmcaRequestId), {
        counterClaim,
        status: 'counter_claim_received',
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error submitting counter-claim:', error);
      throw error;
    }
  }

  // Get All Reports (Admin)
  static async getAllContentReports(): Promise<ContentReport[]> {
    try {
      const q = query(
        collection(db, 'contentReports'),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ContentReport));
    } catch (error) {
      console.error('Error fetching content reports:', error);
      throw error;
    }
  }

  // Get All User Reports (Admin)
  static async getAllUserReports(): Promise<UserReport[]> {
    try {
      const q = query(
        collection(db, 'userReports'),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as UserReport));
    } catch (error) {
      console.error('Error fetching user reports:', error);
      throw error;
    }
  }

  // Get All DMCA Requests (Admin)
  static async getAllDMCARequests(): Promise<DMCARequest[]> {
    try {
      const q = query(
        collection(db, 'dmcaRequests'),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as DMCARequest));
    } catch (error) {
      console.error('Error fetching DMCA requests:', error);
      throw error;
    }
  }

  // Update Report Status
  static async updateReportStatus(
    reportId: string,
    status: ContentReport['status'],
    adminNotes?: string,
    resolvedBy?: string
  ): Promise<void> {
    try {
      const updateData: any = {
        status,
        updatedAt: serverTimestamp()
      };

      if (adminNotes) updateData.adminNotes = adminNotes;
      if (resolvedBy) updateData.resolvedBy = resolvedBy;
      if (status === 'resolved') updateData.resolvedAt = serverTimestamp();

      await updateDoc(doc(db, 'contentReports', reportId), updateData);
    } catch (error) {
      console.error('Error updating report status:', error);
      throw error;
    }
  }

  // Issue User Violation
  static async issueUserViolation(
    userId: string,
    userName: string,
    violationType: UserViolation['violationType'],
    description: string,
    severity: UserViolation['severity'],
    contentId: string | undefined,
    reportId: string | undefined,
    adminNotes: string | undefined,
    issuedBy: string,
    expiresAt?: any
  ): Promise<string> {
    try {
      const violationData: Omit<UserViolation, 'id'> = {
        userId,
        userName,
        violationType,
        description,
        severity,
        contentId,
        reportId,
        adminNotes,
        issuedBy,
        issuedAt: serverTimestamp(),
        expiresAt,
        status: 'active'
      };

      const docRef = await addDoc(collection(db, 'userViolations'), violationData);
      return docRef.id;
    } catch (error) {
      console.error('Error issuing user violation:', error);
      throw error;
    }
  }

  // Get User Violations
  static async getUserViolations(userId: string): Promise<UserViolation[]> {
    try {
      const q = query(
        collection(db, 'userViolations'),
        where('userId', '==', userId),
        orderBy('issuedAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as UserViolation));
    } catch (error) {
      console.error('Error fetching user violations:', error);
      throw error;
    }
  }

  // Get Moderation Stats
  static async getModerationStats(): Promise<ModerationStats> {
    try {
      // Get content reports
      const contentReportsQuery = query(collection(db, 'contentReports'));
      const contentReportsSnapshot = await getDocs(contentReportsQuery);
      const contentReports = contentReportsSnapshot.docs.map(doc => doc.data() as ContentReport);

      // Get user reports
      const userReportsQuery = query(collection(db, 'userReports'));
      const userReportsSnapshot = await getDocs(userReportsQuery);
      const userReports = userReportsSnapshot.docs.map(doc => doc.data() as UserReport);

      // Get DMCA requests
      const dmcaQuery = query(collection(db, 'dmcaRequests'));
      const dmcaSnapshot = await getDocs(dmcaQuery);
      const dmcaRequests = dmcaSnapshot.docs.map(doc => doc.data() as DMCARequest);

      // Get user violations
      const violationsQuery = query(collection(db, 'userViolations'));
      const violationsSnapshot = await getDocs(violationsQuery);
      const violations = violationsSnapshot.docs.map(doc => doc.data() as UserViolation);

      return {
        totalReports: contentReports.length + userReports.length,
        pendingReports: contentReports.filter(r => r.status === 'pending').length + 
                       userReports.filter(r => r.status === 'pending').length,
        resolvedReports: contentReports.filter(r => r.status === 'resolved').length + 
                        userReports.filter(r => r.status === 'resolved').length,
        totalDMCARequests: dmcaRequests.length,
        pendingDMCARequests: dmcaRequests.filter(r => r.status === 'pending').length,
        totalViolations: violations.length,
        activeViolations: violations.filter(v => v.status === 'active').length,
        bannedUsers: violations.filter(v => v.severity === 'permanent_ban' && v.status === 'active').length,
        suspendedUsers: violations.filter(v => v.severity === 'suspension' && v.status === 'active').length
      };
    } catch (error) {
      console.error('Error fetching moderation stats:', error);
      throw error;
    }
  }

  // Helper method to determine priority
  private static determinePriority(reportType: string): 'low' | 'medium' | 'high' | 'urgent' {
    switch (reportType) {
      case 'copyright':
      case 'hate_speech':
      case 'violence':
        return 'urgent';
      case 'harassment':
      case 'inappropriate':
        return 'high';
      case 'spam':
        return 'medium';
      default:
        return 'low';
    }
  }
}
